import { useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@shadcn-in-nx/ui/tabs";
import PmfScoreCard from "./PmfScoreCard";
import PmfSegmentation from "./PmfSegmentation";
import PmfInsights from "./PmfInsights";
import PmfRoadmap from "./PmfRoadmap";
import PmfTextResponses from "./PmfTextResponses";
import PmfSendCampaign from "./PmfSendCampaign";

export default function PmfSurveyDashboard() {
    const { t } = useTranslation();
    const { call: fetchResults, response, isLoading } = useBluelagoon({ method: "getSurveyResults" });

    useEffect(() => { fetchResults(); }, [fetchResults]);
    const refresh = useCallback(() => fetchResults(), [fetchResults]);
    const results = response?.data;

    if (isLoading && !results) {
        return (
            <div className="p-6 flex items-center justify-center">
                <div className="animate-spin h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full" />
            </div>
        );
    }

    const TAB_STYLE = "text-xs data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-violet-600 data-[state=active]:text-violet-600 rounded-none";

    return (
        <div className="p-6 max-w-6xl mx-auto">
            <h1 className="text-2xl font-bold text-slate-900 mb-6">{t("survey.admin.title")}</h1>
            <Tabs defaultValue="score">
                <TabsList className="grid w-full grid-cols-6 rounded-none border-b bg-transparent h-9 mb-6">
                    <TabsTrigger value="score" className={TAB_STYLE}>{t("survey.admin.tabs.score")}</TabsTrigger>
                    <TabsTrigger value="segment" className={TAB_STYLE}>{t("survey.admin.tabs.segment")}</TabsTrigger>
                    <TabsTrigger value="insights" className={TAB_STYLE}>{t("survey.admin.tabs.insights")}</TabsTrigger>
                    <TabsTrigger value="roadmap" className={TAB_STYLE}>{t("survey.admin.tabs.roadmap")}</TabsTrigger>
                    <TabsTrigger value="responses" className={TAB_STYLE}>{t("survey.admin.tabs.responses")}</TabsTrigger>
                    <TabsTrigger value="campaign" className={TAB_STYLE}>{t("survey.admin.tabs.campaign")}</TabsTrigger>
                </TabsList>
                <TabsContent value="score">{results && <PmfScoreCard pmfScore={results.pmfScore} total={results.total} breakdown={results.breakdown} />}</TabsContent>
                <TabsContent value="segment">{results && <PmfSegmentation responses={results.responses} originalScore={results.pmfScore} />}</TabsContent>
                <TabsContent value="insights">{results && <PmfInsights responses={results.responses} />}</TabsContent>
                <TabsContent value="roadmap"><PmfRoadmap /></TabsContent>
                <TabsContent value="responses">{results && <PmfTextResponses responses={results.responses} />}</TabsContent>
                <TabsContent value="campaign">
                    <div className="space-y-4">
                        <PmfSendCampaign variant="initial" onSent={refresh} />
                        <PmfSendCampaign variant="followup" onSent={refresh} />
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}
